import { FunnelStep } from "components/form-funnel/types";
import PositionsFunnelField from "features/positions/PositionsFunnelField";
import SalaryChangeDateField from "./SalaryChangeDateField";

export const step1Steps: FunnelStep[] = [
  {
    id: "personal-info",
    title: { lt: "Asmeninė informacija", en: "Personal info" },
    fields: [
      { name: "firstName", type: "text", label: { lt: "Vardas", en: "First name" }, required: true },
      { name: "lastName", type: "text", label: { lt: "Pavardė", en: "Last name" }, required: true },
      {
        name: "gender", type: "select", label: { lt: "Lytis", en: "Gender" }, required: true,
        helpText: {
          lt: "Naudojama, kad sutartyje pareigos būtų nurodytos tinkama gimine.",
          en: "Used to print the position title in the correct form in the contract.",
        },
        options: [
          { value: "male", label: { lt: "Vyras", en: "Male" } },
          { value: "female", label: { lt: "Moteris", en: "Female" } },
        ],
      },
      { name: "personalEmail", type: "email", label: { lt: "Asmeninis el. paštas", en: "Personal email" }, required: true },
      { name: "personalPhone", type: "phone", label: { lt: "Telefonas", en: "Phone" }, required: true },
      { name: "hiringDate", type: "date", label: { lt: "Įdarbinimo data", en: "Hiring date" }, required: true },
      { name: "birthDate", type: "date", label: { lt: "Gimimo data", en: "Date of birth" } },
    ],
  },
  {
    id: "position-compensation",
    title: { lt: "Pareigos ir atlyginimas", en: "Position & compensation" },
    fields: [
      { name: "workLocation", type: "text", label: { lt: "Darbo vieta", en: "Work location" }, required: true },
      // Chosen from the positions catalogue; the backend derives positionLT/EN.
      { name: "positionIds", type: "custom", label: { lt: "Pareigos", en: "Positions" }, required: true, component: PositionsFunnelField },
      { name: "salary", type: "number", sensitive: true, label: { lt: "Atlyginimas neatskaičius mokesčių", en: "Salary (gross)" }, required: true },
      { name: "hoursPerWeek", type: "number", label: { lt: "Valandos per savaitę", en: "Hours per week" } },
    ],
  },
  {
    id: "contract",
    title: { lt: "Sutartis", en: "Contract" },
    fields: [
      { name: "probationMonths", type: "number", label: { lt: "Bandomasis laikotarpis (mėn.)", en: "Probation (months)" } },
      {
        name: "hasSalaryChange", type: "checkbox",
        label: {
          lt: "Po bandomojo laikotarpio ar nuo tam tikros datos taikomas kitas atlyginimas",
          en: "A different salary applies after probation or from a date",
        },
      },
      {
        name: "salaryChangeDate", type: "custom", required: true,
        label: { lt: "Naujo atlyginimo data", en: "New salary date" },
        component: SalaryChangeDateField,
        visibleWhen: { field: "hasSalaryChange", operator: "eq", value: true },
      },
      {
        name: "salaryAfterChange", type: "number", sensitive: true, required: true,
        label: { lt: "Naujas atlyginimas neatskaičius mokesčių", en: "New salary (gross)" },
        visibleWhen: { field: "hasSalaryChange", operator: "eq", value: true },
      },
      {
        name: "contractVariant", type: "select", label: { lt: "Sutarties variantas", en: "Contract variant" }, required: true,
        options: [
          { value: "standard", label: { lt: "Standartinė", en: "Standard" } },
          { value: "summed", label: { lt: "Suminė", en: "Summed working time" } },
          { value: "director", label: { lt: "Direktoriaus", en: "Director" } },
        ],
      },
      {
        name: "contractType", type: "select", label: { lt: "Sutarties tipas", en: "Contract type" }, required: true,
        options: [
          { value: "indefinite", label: { lt: "Neterminuota", en: "Indefinite" } },
          { value: "fixed_term", label: { lt: "Terminuota", en: "Fixed term" } },
          { value: "other", label: { lt: "Kita", en: "Other" } },
        ],
      },
      {
        name: "contractEndDate", type: "date", label: { lt: "Sutarties pabaigos data", en: "Contract end date" }, required: true,
        visibleWhen: { field: "contractType", operator: "eq", value: "fixed_term" },
      },
      {
        name: "contractTypeOther", type: "text", label: { lt: "Kita sutarties tipas", en: "Other contract type" }, required: true,
        visibleWhen: { field: "contractType", operator: "eq", value: "other" },
      },
    ],
  },
];

export const step3Steps: FunnelStep[] = [
  {
    id: "personal-data",
    title: { lt: "Asmeniniai duomenys", en: "Personal data" },
    fields: [
      { name: "firstName", type: "text", label: { lt: "Vardas", en: "First name" }, required: true },
      { name: "lastName", type: "text", label: { lt: "Pavardė", en: "Last name" }, required: true },
      { name: "personalCode", type: "text", inputMode: "numeric", label: { lt: "Asmens kodas", en: "Personal code" }, required: true, validation: { pattern: "^[0-9]{11}$" } },
      { name: "birthDate", type: "date", label: { lt: "Gimimo data", en: "Date of birth" } },
    ],
  },
  {
    id: "id-document",
    title: { lt: "Asmens dokumentas", en: "ID document" },
    fields: [
      {
        name: "idDocumentType", type: "select", label: { lt: "Dokumento tipas", en: "Document type" }, required: true,
        options: [
          { value: "id_card", label: { lt: "Asmens tapatybės kortelė", en: "ID card" } },
          { value: "passport", label: { lt: "Pasas", en: "Passport" } },
        ],
      },
      { name: "idDocumentNumber", type: "text", label: { lt: "Dokumento Nr.", en: "Document No." }, required: true },
      { name: "idDocumentIssueDate", type: "date", label: { lt: "Išdavimo data", en: "Issue date" }, required: true },
      { name: "idDocumentExpiryDate", type: "date", label: { lt: "Galiojimo data", en: "Expiry date" }, required: true },
      { name: "idDocumentIssuePlace", type: "text", label: { lt: "Išdavimo vieta", en: "Issue place" }, required: true },
    ],
  },
  {
    id: "residence-permit",
    title: { lt: "Leidimas gyventi (pildo užsienio šalių piliečiai)", en: "Residence permit (for foreign citizens only)" },
    skippable: true,
    fields: [
      { name: "residencePermitNumber", type: "text", label: { lt: "Leidimo Nr.", en: "Permit No." } },
      { name: "residencePermitIssueDate", type: "date", label: { lt: "Išdavimo data", en: "Issue date" } },
      { name: "residencePermitExpiryDate", type: "date", label: { lt: "Galiojimo data", en: "Expiry date" } },
      { name: "residencePermitIssuePlace", type: "text", label: { lt: "Išdavimo vieta", en: "Issue place" } },
    ],
  },
  {
    id: "education",
    title: { lt: "Išsilavinimas", en: "Education" },
    fields: [
      {
        name: "educationLevel", type: "select", label: { lt: "Išsilavinimo lygis", en: "Education level" }, required: true,
        options: [
          { value: "primary", label: { lt: "Pradinis", en: "Primary" } },
          { value: "basic", label: { lt: "Pagrindinis", en: "Basic" } },
          { value: "secondary", label: { lt: "Vidurinis", en: "Secondary" } },
          { value: "vocational", label: { lt: "Profesinis", en: "Vocational" } },
          { value: "vocational_secondary", label: { lt: "Profesinis vidurinis", en: "Vocational secondary" } },
          { value: "higher_non_university", label: { lt: "Aukštasis neuniversitetinis", en: "Higher non-university" } },
          { value: "higher_university", label: { lt: "Aukštasis universitetinis", en: "Higher university" } },
          { value: "studying", label: { lt: "Studijuoja", en: "Studying" } },
        ],
      },
      { name: "educationInstitution", type: "text", label: { lt: "Mokymo įstaiga", en: "Institution" } },
      { name: "educationSpecialty", type: "text", label: { lt: "Specialybė", en: "Specialty" } },
    ],
  },
  {
    id: "address-contacts",
    title: { lt: "Adresas ir kontaktai", en: "Address & contacts" },
    fields: [
      { name: "declaredAddress", type: "text", label: { lt: "Deklaruotas adresas", en: "Declared address" }, required: true },
      { name: "actualAddress", type: "text", label: { lt: "Faktinis adresas", en: "Actual address" }, required: true },
      { name: "personalPhone", type: "phone", label: { lt: "Telefonas", en: "Phone" }, required: true },
      // The kiosk login's second factor: only the employer sets it, the server
      // ignores it from the employee.
      { name: "personalEmail", type: "email", label: { lt: "El. paštas", en: "Email" }, readOnly: true },
      { name: "emergencyContact", type: "text", label: { lt: "Asmuo, su kuriuo galėtume susisiekti ypatingu atveju", en: "Person to contact in case of emergency" }, helpText: { lt: "Nurodykite vardą, pavardę, el. paštą, tel. nr.", en: "Enter name, surname, email, phone number" }, placeholder: { lt: "Pvz.: Jonas Jonaitis, jonas@mail.lt, +37060000000", en: "E.g.: John Doe, john@mail.com, +37060000000" }, required: true },
    ],
  },
  {
    id: "family",
    title: { lt: "Šeiminė padėtis", en: "Family" },
    fields: [
      { name: "hasChildrenUnder12", type: "toggle", label: { lt: "Turiu vaikų iki 12 m.", en: "Have children under 12" } },
      { name: "hasChildrenUnder14", type: "toggle", label: { lt: "Turiu vaikų iki 14 m.", en: "Have children under 14" } },
      { name: "hasDisabledChildrenUnder18", type: "toggle", label: { lt: "Turiu neįgalių vaikų iki 18 m.", en: "Have disabled children under 18" } },
      {
        name: "children", type: "repeater", label: { lt: "Vaikai", en: "Children" },
        repeaterFields: [
          { name: "firstName", type: "text", label: { lt: "Vardas", en: "First name" }, required: true },
          { name: "lastName", type: "text", label: { lt: "Pavardė", en: "Last name" }, required: true },
          { name: "personalCode", type: "text", inputMode: "numeric", label: { lt: "Asmens kodas", en: "Personal code" } },
        ],
      },
    ],
  },
  {
    id: "health",
    title: { lt: "Sveikatos būklė", en: "Health" },
    fields: [
      { name: "hasDisability", type: "toggle", label: { lt: "Turiu neįgalumą", en: "Have disability" }, required: "answered" },
      {
        name: "disabilityCertNumber", type: "text", label: { lt: "Pažymėjimo Nr.", en: "Certificate No." },
        visibleWhen: { field: "hasDisability", operator: "eq", value: true },
      },
      {
        name: "disabilityCertExpiry", type: "date", label: { lt: "Galioja iki", en: "Valid until" },
        visibleWhen: { field: "hasDisability", operator: "eq", value: true },
      },
      {
        name: "workCapacityPercent", type: "number", label: { lt: "Darbingumo lygis (%)", en: "Work capacity (%)" },
        visibleWhen: { field: "hasDisability", operator: "eq", value: true },
      },
    ],
  },
  {
    id: "additional-info",
    title: { lt: "Papildoma informacija", en: "Additional info" },
    fields: [
      {
        name: "hasConflictOfInterest", type: "select", label: { lt: "Ar dirbate kitoje įmonėje, viešojoje įstaigoje arba tiesiogiai ar netiesiogiai esate įgiję joje kontrolę, turite nuosavybės teise priklausančių akcijų ar kitokio pobūdžio interesų?", en: "Do you work at another company, public institution, or directly/indirectly have control, own shares, or have other interests?" }, required: true,
        helpText: { lt: "Siekiant išsaugoti gerą įmonės vardą ir garantuoti darbuotojų veiksmų skaidrumą, vengti darbuotojų ir klientų interesų susikirtimo, nurodykite galimus interesų konfliktus.", en: "To maintain the company's reputation and ensure transparency, avoid conflicts of interest between employees and clients." },
        options: [
          { value: "yes", label: { lt: "Taip", en: "Yes" } },
          { value: "no", label: { lt: "Ne", en: "No" } },
        ],
      },
      {
        name: "conflictOfInterestDetails", type: "textarea", label: { lt: "Interesų konflikto aprašymas", en: "Conflict details" },
        helpText: { lt: "Įrašykite įmonės (įstaigos) pavadinimą, kitą svarbią informaciją", en: "Enter company (institution) name and other important information" },
        visibleWhen: { field: "hasConflictOfInterest", operator: "eq", value: "yes" },
      },
      { name: "additionalInfo", type: "textarea", label: { lt: "Kokią papildomą informaciją turėtų žinoti Darbdavys apie galimas priežastis, kurios gali trukdyti Jums atlikti numatomas darbo funkcijas?", en: "What additional information should the Employer know about possible reasons that may prevent you from performing your job duties?" } },
      {
        name: "dataProcessingConsent", type: "select", label: { lt: "Sutinku / nesutinku, kad Darbuotojo anketoje pateikti asmens duomenys būtų naudojami siekiant tvarkyti mano, kaip darbuotojo, asmens bylą", en: "I agree / disagree that personal data provided in the Employee questionnaire be used to manage my employee personal file" }, required: true,
        helpText: { lt: "Man yra žinomos mano, kaip duomenų subjekto, teisės, nustatytos Lietuvos Respublikos asmens duomenų teisinės apsaugos įstatyme.", en: "I am aware of my rights as a data subject under the Law on Legal Protection of Personal Data of the Republic of Lithuania." },
        options: [
          { value: "agree", label: { lt: "Sutinku", en: "Agree" } },
          { value: "disagree", label: { lt: "Nesutinku", en: "Disagree" } },
        ],
      },
    ],
  },
];

export const step4Steps: FunnelStep[] = [
  {
    id: "review",
    title: { lt: "Peržiūra", en: "Review" },
    fields: [
      { name: "firstName", type: "text", label: { lt: "Vardas", en: "First name" }, readOnly: true },
      { name: "lastName", type: "text", label: { lt: "Pavardė", en: "Last name" }, readOnly: true },
      { name: "personalCode", type: "text", label: { lt: "Asmens kodas", en: "Personal code" }, readOnly: true },
      { name: "positionLT", type: "text", label: { lt: "Pareigos", en: "Position" }, readOnly: true },
      { name: "workLocation", type: "text", label: { lt: "Darbo vieta", en: "Work location" }, readOnly: true },
    ],
  },
  {
    id: "bank-payments",
    title: { lt: "Banko ir mokėjimai", en: "Bank & payments" },
    fields: [
      { name: "bankAccount", type: "text", label: { lt: "Banko sąskaita (IBAN)", en: "Bank account (IBAN)" }, required: true },
      {
        name: "paymentFrequency", type: "select", label: { lt: "Mokėjimo dažnumas", en: "Payment frequency" }, required: true,
        options: [
          { value: "once_per_month", label: { lt: "Kartą per mėnesį", en: "Once per month" } },
          { value: "twice_per_month", label: { lt: "Du kartus per mėnesį", en: "Twice per month" } },
        ],
      },
      {
        name: "npdOption", type: "select", label: { lt: "NPD", en: "NPD" }, required: true,
        options: [
          { value: "no_npd", label: { lt: "Netaikyti NPD", en: "No NPD" } },
          { value: "standard_npd", label: { lt: "Taikyti NPD", en: "Standard NPD" } },
        ],
      },
      {
        name: "disabilityLevel", type: "select", label: { lt: "Neįgalumo lygis", en: "Disability level" },
        options: [
          { value: "0_25", label: { lt: "0-25%", en: "0-25%" } },
          { value: "30_55", label: { lt: "30-55%", en: "30-55%" } },
        ],
      },
    ],
  },
];

export const step5Steps: FunnelStep[] = [
  {
    id: "gdpr-consents",
    title: { lt: "BDAR sutikimai", en: "GDPR consents" },
    description: {
      lt: "Jei sutinkate – pažymėkite varnele. Jei nesutinkate – nežymėkite varnelės.",
      en: "If you agree, check the box. If you disagree, leave the box unchecked.",
    },
    fields: [
      {
        name: "gdprConsent1BirthdayGreeting",
        type: "toggle",
        label: { lt: "Gimtadienio sveikinimas", en: "Birthday greeting" },
        helpText: {
          lt: "Darbuotojų skatinimo, tarpusavio bendravimo palaikymo, sveikinimo (pavyzdžiui, gimtadienio paminėjimo) tikslais, sutinku, kad būtų tvarkomi mano vardas, pavardė, gimimo mėnuo ir diena. Esu informuotas, kad Bendrovės vidinėje komunikacijoje bus pasidalinta šia informacija.",
          en: "For employee encouragement, maintaining communication, and greeting purposes (e.g. birthday celebration), I consent to the processing of my name, surname, birth month and day. I am informed that this information will be shared in the Company's internal communication.",
        },
      },
      {
        name: "gdprConsent2PhotoSessions",
        type: "toggle",
        label: { lt: "Foto sesijos", en: "Photo sessions" },
        helpText: {
          lt: "Bendrovės veiklos viešinimo, išorinės reprezentacijos tikslais, sutinku, kad mane fotografuotų Bendrovės organizuojamų specialių fotosesijų metu ir Bendrovės vidinių ar išorinių renginių metu bei šios nuotraukos būtų saugomos Bendrovės valdomuose IT resursuose.",
          en: "For Company publicity and external representation purposes, I consent to being photographed during Company-organized photo sessions and internal or external events, and for these photos to be stored in Company-managed IT resources.",
        },
      },
      {
        name: "gdprConsent3Website",
        type: "toggle",
        label: { lt: 'Svetainės "Komanda"', en: "Website team page" },
        helpText: {
          lt: 'Bendrovės veiklos viešinimo ir išorinės reprezentacijos tikslais, sutinku, kad mano atvaizdas bei mano vardas, pavardė ir pareigos būtų viešinami Bendrovės internetinio tinklalapio skiltyje „Komanda".',
          en: "For Company publicity and external representation purposes, I consent to my image, name, surname and position being published on the Company's website \"Team\" section.",
        },
      },
      {
        name: "gdprConsent4SocialMedia",
        type: "toggle",
        label: { lt: "Socialiniai tinklai", en: "Social media" },
        helpText: {
          lt: 'Bendrovės veiklos viešinimo ir išorinės reprezentacijos tikslais, sutinku, kad mano atvaizdas bei mano vardas, pavardė ir pareigos būtų viešinami Bendrovės valdomose socialinių tinklų (įskaitant bet neapsiribojant „LinkedIn" ir „Facebook") paskyrose.',
          en: "For Company publicity and external representation purposes, I consent to my image, name, surname and position being published on Company-managed social media accounts (including but not limited to LinkedIn and Facebook).",
        },
      },
      {
        name: "gdprConsent5ClientPresentations",
        type: "toggle",
        label: { lt: "Klientų prezentacijos", en: "Client presentations" },
        helpText: {
          lt: "Bendrovės veiklos viešinimo ir išorinės reprezentacijos tikslais, sutinku, kad mano atvaizdas būtų viešinamas Bendrovės klientams pateikiamose prezentacijose, pristatant Bendrovės atstovus.",
          en: "For Company publicity and external representation purposes, I consent to my image being used in presentations to Company clients when introducing Company representatives.",
        },
      },
      {
        name: "gdprConsent6InternalPhotoVideo",
        type: "toggle",
        label: { lt: "Vidinis foto/video", en: "Internal photo/video" },
        helpText: {
          lt: "Darbuotojų skatinimo, tarpusavio bendravimo palaikymo tikslais, sutinku, kad mane fotografuotų ir (ar) filmuotų Bendrovės vidinių renginių metu bei šios nuotraukos ir (ar) vaizdo įrašai būtų naudojami Bendrovės vidinėje komunikacijoje.",
          en: "For employee encouragement and maintaining communication purposes, I consent to being photographed and/or filmed during Company internal events and for these photos and/or videos to be used in Company internal communication.",
        },
      },
      {
        name: "gdprConsent7InternalNetwork",
        type: "toggle",
        label: { lt: "Vidinis tinklas", en: "Internal network" },
        helpText: {
          lt: "Darbuotojų tarpusavio bendravimo palaikymo, identifikavimo tikslais, sutinku, kad mano atvaizdas bei mano vardas, pavardė ir pareigos būtų naudojami Bendrovės vidiniame tinkle (intranete) ir vidiniuose komunikacijos įrankiuose.",
          en: "For employee communication and identification purposes, I consent to my image, name, surname and position being used in the Company's internal network (intranet) and internal communication tools.",
        },
      },
    ],
  },
];

/** v2 combined employee form: personal data + bank/payments + GDPR (replaces v1 step3+step4+step5) */
export const step2Steps: FunnelStep[] = [
  ...step3Steps,
  ...step4Steps,
  ...step5Steps,
];

/** Initial values of the combined employee form, from the onboarding record. */
export function step2InitialData(ob: Record<string, any>): Record<string, any> {
  return {
    ...ob,
    hasConflictOfInterest:
      ob.hasConflictOfInterest === true
        ? "yes"
        : ob.hasConflictOfInterest === false
        ? "no"
        : ob.hasConflictOfInterest,
    // GDPR toggles: null → false (not agreed by default)
    gdprConsent1BirthdayGreeting: ob.gdprConsent1BirthdayGreeting ?? false,
    gdprConsent2PhotoSessions: ob.gdprConsent2PhotoSessions ?? false,
    gdprConsent3Website: ob.gdprConsent3Website ?? false,
    gdprConsent4SocialMedia: ob.gdprConsent4SocialMedia ?? false,
    gdprConsent5ClientPresentations: ob.gdprConsent5ClientPresentations ?? false,
    gdprConsent6InternalPhotoVideo: ob.gdprConsent6InternalPhotoVideo ?? false,
    gdprConsent7InternalNetwork: ob.gdprConsent7InternalNetwork ?? false,
  };
}

/**
 * What HR may fill in the employee form before the employee checks and
 * submits it: facts HR already holds, plus the children list. Mirrors
 * HR_PREFILL_FIELDS_STEP_2 of the backend (onboarding/employee-field-maps.js),
 * which is the authority and ignores any other field. Declarations, health,
 * payment choices and consents stay the employee's own.
 */
export const HR_PREFILL_FIELDS: string[] = [
  "firstName", "lastName", "personalCode", "birthDate", "personalPhone",
  "idDocumentType", "idDocumentNumber", "idDocumentIssueDate",
  "idDocumentExpiryDate", "idDocumentIssuePlace",
  "residencePermitNumber", "residencePermitIssueDate",
  "residencePermitExpiryDate", "residencePermitIssuePlace",
  "educationLevel", "educationInstitution", "educationSpecialty",
  "declaredAddress", "actualAddress", "emergencyContact",
  "hasChildrenUnder12", "hasChildrenUnder14", "hasDisabledChildrenUnder18",
  "children",
  "bankAccount",
];

/**
 * The employee form as HR fills it: only the editable HR fields, none of them
 * required (HR fills what they know, the employee completes the rest), and
 * without the steps left empty.
 */
export const hrPrefillSteps: FunnelStep[] = step2Steps
  .map((step) => ({
    ...step,
    fields: step.fields
      .filter((field) => HR_PREFILL_FIELDS.includes(field.name) && !field.readOnly)
      .map((field) => ({ ...field, required: false })),
  }))
  .filter((step) => step.fields.length > 0);
